// ============================================================
// E0：编辑器命令总线（生产化）
// - 命令注册表 + 幂等执行（commandId 去重）
// - 事务：整段命令 = 1 个撤销步；失败自动整批回滚
// - 复用现有两套历史后端：
//   3D  → sceneStore 快照（事务期挂起自动快照，提交时统一压栈）
//   2D  → dashboardHistory 手势批次（beginGestureHistory/endGestureHistory）
// - 供 E1/E2/C1（编辑器内 AI / 渲染预设）程序化驱动编辑器
// ============================================================
import type { Model3D, VisualComponent } from '@dtwin/shared';
import useSceneStore, {
  applySceneSnapshot,
  captureSceneSnapshot,
  setSceneHistorySuspended,
  type SceneSimulationState,
  type SceneSnapshot,
} from '../stores/sceneStore';
import useDashboardStore from '../stores/dashboardStore';
import { beginGestureHistory, endGestureHistory, useDashboardHistory } from '../stores/dashboardHistory';
import useHistoryStore from '../stores/historyStore';
import useEditorUiStore from '../stores/editorUiStore';

export type CommandScope = 'scene' | 'dashboard';

export interface CommandSpec {
  type: string;
  payload?: unknown;
  /** 幂等 ID：重复执行同一 ID 直接返回成功（不重复改状态） */
  id?: string;
}

export interface CommandResult {
  ok: boolean;
  type: string;
  id?: string;
  error?: string;
  deduped?: boolean;
}

export interface BatchResult {
  ok: boolean;
  results: CommandResult[];
  error?: string;
  deduped?: boolean;
}

export interface CommandDefinition<P = unknown> {
  type: string;
  scope: CommandScope;
  validate?: (payload: P) => string | null;
  apply: (payload: P) => void;
}

const MAX_SEEN_IDS = 1000;

interface TxState {
  label: string;
  depth: number;
  sceneActive: boolean;
  dashActive: boolean;
  sceneSnapshot?: SceneSnapshot;
}

function req(payload: unknown, keys: string[]): string | null {
  if (!payload || typeof payload !== 'object') return 'payload 需为对象';
  const o = payload as Record<string, unknown>;
  for (const k of keys) {
    if (o[k] === undefined || o[k] === null) return `缺少字段：${k}`;
  }
  return null;
}

class CommandBus {
  private defs = new Map<string, CommandDefinition>();
  private seenIds: string[] = [];
  private seenSet = new Set<string>();
  private batchSeen = new Set<string>();
  private tx: TxState | null = null;

  register<P>(def: CommandDefinition<P>) {
    this.defs.set(def.type, def as CommandDefinition);
  }

  list(): Array<{ type: string; scope: CommandScope }> {
    return [...this.defs.values()].map((d) => ({ type: d.type, scope: d.scope }));
  }

  inTransaction(): boolean {
    return this.tx !== null;
  }

  // ── 执行 ──

  async execute(spec: CommandSpec): Promise<CommandResult> {
    const def = this.defs.get(spec.type);
    if (!def) return { ok: false, type: spec.type, ...(spec.id ? { id: spec.id } : {}), error: `未注册命令：${spec.type}` };
    if (spec.id && this.seenSet.has(spec.id)) {
      return { ok: true, type: spec.type, id: spec.id, deduped: true };
    }
    const verr = def.validate?.(spec.payload);
    if (verr) return { ok: false, type: spec.type, ...(spec.id ? { id: spec.id } : {}), error: verr };

    const run = () => def.apply(spec.payload);
    try {
      if (this.tx) {
        this.touchScope(def.scope);
        run();
      } else {
        // 单命令隐式事务：统一为 1 个撤销步
        this.beginTransaction(def.type);
        this.touchScope(def.scope);
        run();
        const c = this.commitTransaction();
        if (!c.ok) return { ok: false, type: spec.type, ...(spec.id ? { id: spec.id } : {}), error: c.error };
      }
    } catch (e) {
      // 事务内失败：整批回滚，保证原子性
      if (this.tx) this.rollbackInternal();
      return { ok: false, type: spec.type, ...(spec.id ? { id: spec.id } : {}), error: (e as Error)?.message || String(e) };
    }
    if (spec.id) this.rememberId(spec.id);
    return { ok: true, type: spec.type, ...(spec.id ? { id: spec.id } : {}) };
  }

  /** 批量执行（E1/E2/C1 主入口）：整批一个事务、一个撤销步；失败整批回滚 */
  async applyBatch(specs: CommandSpec[], opts: { label?: string; transactionId?: string } = {}): Promise<BatchResult> {
    if (opts.transactionId && this.batchSeen.has(opts.transactionId)) {
      return { ok: true, results: [], deduped: true };
    }
    this.beginTransaction(opts.label ?? 'batch');
    const results: CommandResult[] = [];
    let error: string | undefined;
    for (const spec of specs) {
      const r = await this.execute(spec);
      results.push(r);
      if (!r.ok) {
        error = r.error;
        break;
      }
    }
    if (error) {
      this.rollbackTransaction();
      return { ok: false, results, error };
    }
    const c = this.commitTransaction();
    if (!c.ok) return { ok: false, results, error: c.error };
    if (opts.transactionId) this.rememberBatch(opts.transactionId);
    return { ok: true, results };
  }

  // ── 事务 ──

  beginTransaction(label = 'tx'): void {
    if (this.tx) {
      this.tx.depth += 1;
      return;
    }
    this.tx = { label, depth: 1, sceneActive: false, dashActive: false };
  }

  commitTransaction(): { ok: boolean; error?: string } {
    const tx = this.tx;
    if (!tx) return { ok: false, error: '无进行中的事务' };
    tx.depth -= 1;
    if (tx.depth > 0) return { ok: true };
    this.tx = null;
    try {
      if (tx.sceneActive) {
        setSceneHistorySuspended(false);
        if (tx.sceneSnapshot) useHistoryStore.getState().pushSnapshot(tx.sceneSnapshot);
      }
      if (tx.dashActive) endGestureHistory();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: (e as Error)?.message || String(e) };
    }
  }

  rollbackTransaction(): void {
    this.rollbackInternal();
  }

  private rollbackInternal(): void {
    const tx = this.tx;
    if (!tx) return;
    this.tx = null;
    if (tx.sceneActive) {
      if (tx.sceneSnapshot) applySceneSnapshot(tx.sceneSnapshot);
      setSceneHistorySuspended(false);
    }
    if (tx.dashActive) {
      useDashboardHistory.getState().undo();
      endGestureHistory();
    }
  }

  private touchScope(scope: CommandScope): void {
    const tx = this.tx;
    if (!tx) return;
    if (scope === 'scene' && !tx.sceneActive) {
      tx.sceneSnapshot = captureSceneSnapshot();
      setSceneHistorySuspended(true);
      tx.sceneActive = true;
    }
    if (scope === 'dashboard' && !tx.dashActive) {
      beginGestureHistory();
      tx.dashActive = true;
    }
  }

  // ── 撤销/重做（按当前激活编辑器路由到既有历史） ──

  undo(): void {
    if (useEditorUiStore.getState().activeEditor === 'dashboard') useDashboardHistory.getState().undo();
    else useSceneStore.getState().undo();
  }

  redo(): void {
    if (useEditorUiStore.getState().activeEditor === 'dashboard') useDashboardHistory.getState().redo();
    else useSceneStore.getState().redo();
  }

  canUndo(): boolean {
    return useEditorUiStore.getState().activeEditor === 'dashboard'
      ? useDashboardHistory.getState().past.length > 0
      : useHistoryStore.getState().canUndo;
  }

  // ── 幂等记录 ──

  private rememberId(id: string) {
    if (this.seenSet.has(id)) return;
    this.seenSet.add(id);
    this.seenIds.push(id);
    if (this.seenIds.length > MAX_SEEN_IDS) {
      const drop = this.seenIds.splice(0, this.seenIds.length - MAX_SEEN_IDS);
      for (const d of drop) this.seenSet.delete(d);
    }
  }

  private rememberBatch(id: string) {
    this.batchSeen.add(id);
    if (this.batchSeen.size > MAX_SEEN_IDS) {
      const first = this.batchSeen.values().next().value as string | undefined;
      if (first) this.batchSeen.delete(first);
    }
  }
}

export const commandBus = new CommandBus();

// ============================================================
// 内置命令集（E0 最小集：场景设置 / 模型 / 子构件 / 大屏组件）
// 说明：命令只改本地文档态（预览语义）；持久化仍走既有保存流程（useSceneSave / savePage）
// ============================================================

function registerBuiltins() {
  const scene = useSceneStore.getState;

  // ── 场景设置 ──
  commandBus.register({
    type: 'scene.environment.set',
    scope: 'scene',
    validate: (p) => req(p, ['patch']),
    apply: (p) => scene().setEnvironment((p as { patch: Record<string, unknown> }).patch as never),
  });
  commandBus.register({
    type: 'scene.effects.set',
    scope: 'scene',
    validate: (p) => req(p, ['patch']),
    apply: (p) => scene().setEffects((p as { patch: Record<string, unknown> }).patch as never),
  });
  commandBus.register({
    type: 'scene.light.add',
    scope: 'scene',
    validate: (p) => req(p, ['light']),
    apply: (p) => scene().addLight((p as { light: never }).light),
  });
  commandBus.register({
    type: 'scene.light.update',
    scope: 'scene',
    validate: (p) => req(p, ['id', 'patch']),
    apply: (p) => {
      const { id, patch } = p as { id: string; patch: Record<string, unknown> };
      scene().updateLight(id, patch as never);
    },
  });
  commandBus.register({
    type: 'scene.light.remove',
    scope: 'scene',
    validate: (p) => req(p, ['id']),
    apply: (p) => scene().removeLight((p as { id: string }).id),
  });
  commandBus.register({
    type: 'scene.grid.set',
    scope: 'scene',
    validate: (p) => {
      const o = (p ?? {}) as Record<string, unknown>;
      return o.showGrid === undefined && o.showGround === undefined ? 'showGrid/showGround 至少一个' : null;
    },
    apply: (p) => {
      const { showGrid, showGround } = p as { showGrid?: boolean; showGround?: boolean };
      if (showGrid !== undefined) scene().setShowGrid(!!showGrid);
      if (showGround !== undefined) scene().setShowGround(!!showGround);
    },
  });
  commandBus.register({
    type: 'scene.displayRegion.set',
    scope: 'scene',
    validate: () => null,
    apply: (p) => scene().setDisplayRegion((p as { region: never }).region ?? null),
  });
  commandBus.register({
    type: 'scene.cameraStrategy.set',
    scope: 'scene',
    validate: (p) => req(p, ['strategy']),
    apply: (p) => scene().setCameraStrategy((p as { strategy: 'auto' | 'current' | 'custom' }).strategy),
  });
  commandBus.register({
    type: 'scene.savedCamera.set',
    scope: 'scene',
    validate: (p) => req(p, ['camera']),
    apply: (p) => scene().setSavedCamera((p as { camera: { position: [number, number, number]; target: [number, number, number] } }).camera),
  });

  // I1：仿真参数包（流体/能耗）应用/清除（可撤销）
  commandBus.register({
    type: 'scene.simulation.set',
    scope: 'scene',
    validate: (p) => (p && typeof p === 'object' && 'simulation' in (p as Record<string, unknown>) ? null : 'simulation 必填（可为 null 表示清除）'),
    apply: (p) => scene().setSimulation(((p as { simulation?: SceneSimulationState | null }).simulation ?? null)),
  });

  // ── 模型 ──
  commandBus.register({
    type: 'model.add',
    scope: 'scene',
    validate: (p) => req(p, ['model']),
    apply: (p) => scene().addModel((p as { model: Omit<Model3D, 'sceneId'> & { id?: string } }).model),
  });
  commandBus.register({
    type: 'model.update',
    scope: 'scene',
    validate: (p) => req(p, ['id', 'patch']),
    apply: (p) => {
      const { id, patch } = p as { id: string; patch: Partial<Model3D> };
      scene().updateModel(id, patch, false);
    },
  });
  commandBus.register({
    type: 'model.transform',
    scope: 'scene',
    validate: (p) => req(p, ['id']),
    apply: (p) => {
      const { id, position, rotation, scale } = p as {
        id: string;
        position?: [number, number, number];
        rotation?: [number, number, number];
        scale?: [number, number, number];
      };
      const patch: Partial<Model3D> = {};
      if (position) patch.position = position;
      if (rotation) patch.rotation = rotation;
      if (scale) patch.scale = scale;
      scene().updateModel(id, patch, false);
    },
  });
  commandBus.register({
    type: 'model.remove',
    scope: 'scene',
    validate: (p) => {
      const o = p as { ids?: unknown };
      return Array.isArray(o?.ids) && o.ids.length > 0 ? null : 'ids 需为非空数组';
    },
    apply: (p) => {
      const { ids } = p as { ids: string[] };
      for (const id of ids) scene().removeModel(id);
    },
  });
  commandBus.register({
    type: 'model.visible.set',
    scope: 'scene',
    validate: (p) => req(p, ['id', 'visible']),
    apply: (p) => {
      const { id, visible } = p as { id: string; visible: boolean };
      scene().updateModel(id, { visible: !!visible }, false);
    },
  });
  commandBus.register({
    type: 'model.locked.set',
    scope: 'scene',
    validate: (p) => req(p, ['id', 'locked']),
    apply: (p) => {
      const { id, locked } = p as { id: string; locked: boolean };
      scene().setLocked(id, !!locked);
    },
  });
  commandBus.register({
    type: 'model.move',
    scope: 'scene',
    validate: (p) => req(p, ['id', 'dir']),
    apply: (p) => {
      const { id, dir } = p as { id: string; dir: 'up' | 'down' | 'top' | 'bottom' };
      scene().moveModel(id, dir);
    },
  });
  commandBus.register({
    type: 'model.device.set',
    scope: 'scene',
    validate: (p) => req(p, ['id']),
    apply: (p) => {
      const { id, deviceId } = p as { id: string; deviceId: string | null };
      scene().updateModel(id, { deviceId: deviceId ?? undefined } as Partial<Model3D>, false);
    },
  });

  // ── 子构件 ──
  commandBus.register({
    type: 'subobject.config.update',
    scope: 'scene',
    validate: (p) => req(p, ['modelId', 'cfg']),
    apply: (p) => {
      const { modelId, cfg } = p as { modelId: string; cfg: { uuid: string } & Record<string, unknown> };
      scene().updateSubObjectConfig(modelId, cfg as never);
    },
  });

  // ── 大屏组件（2D） ──
  const dash = useDashboardStore.getState;
  commandBus.register({
    type: 'page.config.patch',
    scope: 'dashboard',
    validate: (p) => req(p, ['patch']),
    apply: (p) => dash().patchPageConfig((p as { patch: Record<string, unknown> }).patch),
  });
  commandBus.register({
    type: 'widget.add',
    scope: 'dashboard',
    validate: (p) => req(p, ['component']),
    apply: (p) => dash().addComponent((p as { component: Omit<VisualComponent, 'id' | 'pageId'> }).component),
  });
  commandBus.register({
    type: 'widget.update',
    scope: 'dashboard',
    validate: (p) => req(p, ['id', 'patch']),
    apply: (p) => {
      const { id, patch } = p as { id: string; patch: Partial<VisualComponent> };
      dash().updateComponent(id, patch);
    },
  });
  commandBus.register({
    type: 'widget.remove',
    scope: 'dashboard',
    validate: (p) => req(p, ['id']),
    apply: (p) => dash().removeComponent((p as { id: string }).id),
  });
  commandBus.register({
    type: 'widget.duplicate',
    scope: 'dashboard',
    validate: (p) => req(p, ['id']),
    apply: (p) => dash().duplicateComponent((p as { id: string }).id),
  });
  commandBus.register({
    type: 'widget.reorder',
    scope: 'dashboard',
    validate: (p) => req(p, ['id', 'dir']),
    apply: (p) => {
      const { id, dir } = p as { id: string; dir: 'up' | 'down' | 'top' | 'bottom' };
      dash().moveComponentZ(id, dir);
    },
  });
}

registerBuiltins();

// 调试桥（E1/E2 与 e2e 可程序化驱动；仅开发环境挂载）
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  const w = window as unknown as { __commandBus?: CommandBus; __commandBusStores?: unknown };
  w.__commandBus = commandBus;
  w.__commandBusStores = { scene: useSceneStore, dashboard: useDashboardStore };
}

export default commandBus;
